/**
 * Transport: the musical clock. Produced by the analysis worker (beat tracking, tap tempo, MIDI
 * clock, or a file's pre-analysed beat grid) and evaluated by the renderer at any time, including
 * slightly in the future, which is how beat prediction cancels out display latency.
 *
 * All times are AudioContext ("graph") seconds, the same clock as AudioFrame time.
 */

export type TempoSource = 'internal' | 'tap' | 'midi' | 'grid';

export interface TransportState {
  source: TempoSource;
  bpm: number;
  /**
   * The tempo to show people: follows `bpm` slowly and only moves in visible steps, so the readout
   * doesn't flicker while the tracker fine-tunes the period.
   */
  steadyBpm: number;
  /** Seconds per beat. */
  period: number;
  /** Graph time of the most recent beat at or before the tracker's "now". */
  lastBeatTime: number;
  /** Index of the beat at lastBeatTime (monotonic). */
  beatIndex: number;
  /** 0..1: how much the renderer should trust the prediction. */
  confidence: number;
  /** True once a tempo has been established. */
  locked: boolean;
  /** Beats per bar (time signature numerator): detected, or set in Settings. */
  beatsPerBar: number;
  /** Beat index (mod beatsPerBar) of downbeats, from downbeat detection or a resync. */
  barOffset: number;
  /** Beat index at which the current phrase grid is anchored. */
  phraseAnchor: number;
}

const mod = (a: number, n: number) => ((a % n) + n) % n;

/** Bar length when nothing better is known. */
export const BEATS_PER_BAR = 4;

/** Beats per bar of a transport, tolerating old snapshots without the field. */
export const barLength = (s: Pick<TransportState, 'beatsPerBar'>): number =>
  s.beatsPerBar >= 1 ? s.beatsPerBar : BEATS_PER_BAR;

/** Position of the current beat within its bar, 0 = beat one. */
export function beatInBarOf(s: TransportState): number {
  return mod(s.beatIndex - (s.barOffset ?? 0), barLength(s));
}

export function idleTransport(): TransportState {
  return {
    source: 'internal',
    bpm: 120,
    steadyBpm: 120,
    period: 0.5,
    lastBeatTime: 0,
    beatIndex: 0,
    confidence: 0,
    locked: false,
    beatsPerBar: BEATS_PER_BAR,
    barOffset: 0,
    phraseAnchor: 0,
  };
}

export interface TransportPhase {
  /** 0..1 position within the current beat. */
  beatPhase: number;
  /** 0..1 position within the current bar. */
  barPhase: number;
  /** 0..1 position within the current phrase. */
  phrasePhase: number;
  /** Absolute beat index at time t. */
  beat: number;
  /** Beat number within the bar, 0..beatsPerBar-1. */
  beatInBar: number;
  /** Seconds since the most recent beat at time t. */
  sinceBeat: number;
}

/** Evaluate the transport at graph time t (may be past or future). Writes into `out`. */
export function transportPhaseAt(
  s: TransportState,
  t: number,
  out: TransportPhase,
  phraseBars = 16,
): TransportPhase {
  const beats = (t - s.lastBeatTime) / s.period;
  const whole = Math.floor(beats);
  const frac = beats - whole;
  const beat = s.beatIndex + whole;
  const bar = barLength(s);
  const inBar = mod(beat - (s.barOffset ?? 0), bar);
  const phraseBeats = phraseBars * bar;
  out.beatPhase = frac;
  out.barPhase = (inBar + frac) / bar;
  out.phrasePhase = (mod(beat - (s.phraseAnchor ?? 0), phraseBeats) + frac) / phraseBeats;
  out.beat = beat;
  out.beatInBar = inBar;
  out.sinceBeat = frac * s.period;
  return out;
}

export function createPhase(): TransportPhase {
  return { beatPhase: 0, barPhase: 0, phrasePhase: 0, beat: 0, beatInBar: 0, sinceBeat: 0 };
}

/**
 * Graph time of the next boundary at or after time t: the next beat, bar or phrase.
 * Used for launch quantization.
 */
export function nextBoundary(
  s: TransportState,
  t: number,
  unit: 'beat' | 'bar' | 'phrase',
  phraseBars = 16,
): number {
  const p = transportPhaseAt(s, t, createPhase(), phraseBars);
  const bar = barLength(s);
  const beatsLeft =
    unit === 'beat'
      ? 1 - p.beatPhase
      : unit === 'bar'
        ? (1 - p.barPhase) * bar
        : (1 - p.phrasePhase) * phraseBars * bar;
  // Already (almost) on a boundary: go now rather than wait a whole unit.
  const lead = beatsLeft * s.period;
  const full = unit === 'beat' ? 1 : unit === 'bar' ? bar : phraseBars * bar;
  return lead > (full - 0.02) * s.period ? t : t + lead;
}

/**
 * The tempo as shown: always four digits ("098.5", "128.0"), so the readout never changes width,
 * from the steady value so it doesn't flicker. Unlocked: "---.-".
 */
export function formatBpm(s: Pick<TransportState, 'locked' | 'bpm' | 'steadyBpm'>): string {
  if (!s.locked) return '---.-';
  const v = Math.min(999.9, Math.max(0, s.steadyBpm > 0 ? s.steadyBpm : s.bpm));
  return v.toFixed(1).padStart(5, '0');
}

/** "4/4", "3/4", "6/8": the time signature for display. */
export function meterLabel(beats: number): string {
  return beats === 6 || beats === 7 ? `${beats}/8` : `${beats}/4`;
}
